import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import LastChanged, { HistoryLink } from '../components/LastChanged';
import { cs } from '../styles/common';
import { ROLE_INFO } from '../roles';

const ROLES = ['director', 'setter', 'operator'];

const ROLE_META = {
  director: { label: 'Director', color: '#14b8a6', bg: 'rgba(20,184,166,0.12)', border: 'rgba(20,184,166,0.3)' },
  setter:   { label: 'Setter',   color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  operator: { label: 'Operator', color: '#9ca3af', bg: 'rgba(156,163,175,0.1)',  border: 'rgba(156,163,175,0.25)' },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.operator;
  return (
    <span style={{ fontSize: '10px', fontWeight: '700', color: m.color, background: m.bg, border: `1px solid ${m.border}`, borderRadius: '6px', padding: '2px 8px', textTransform: 'capitalize' }}>
      {m.label}
    </span>
  );
}

// ── LIST PAGE ─────────────────────────────────────────────────
export default function AdminUsers() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  function load() {
    setLoading(true); setLoadError('');
    api.get('/api/v1/users')
      .then(r => { setUsers(r.data); setLoading(false); })
      .catch(err => { setLoadError(err.response?.data?.error || 'Failed to load users'); setLoading(false); });
  }

  useEffect(() => { load(); }, []);

  const active   = users.filter(u => u.active);
  const inactive = users.filter(u => !u.active);

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>
        <button onClick={goBack} style={s.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={s.sep}>·</span>
        <span style={s.crumb}>Users</span>
      </div>

      <div style={s.header}>
        <div>
          <h1 style={s.title}>Users</h1>
          <p style={s.sub}>{active.length} active · {inactive.length} inactive</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <HistoryLink type="user" />
          <button style={s.addBtn} onClick={() => setShowAdd(true)}>+ Add User</button>
        </div>
      </div>

      {loadError && <div style={{ background: '#7f1d1d', color: '#fca5a5', border: '1px solid #991b1b', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' }}>{loadError}</div>}
      {loading ? <div style={s.center}>Loading...</div> : (
        <>
          <div style={s.table}>
            <div style={s.tableHeader}>
              <span>Name</span>
              <span>Email</span>
              <span>Role</span>
              <span>Status</span>
            </div>
            {active.length === 0 && (
              <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>No active users.</div>
            )}
            {active.map(u => (
              <UserRow key={u.id} user={u} onClick={() => navigate(`/admin/users/${u.id}`)} />
            ))}
          </div>

          {inactive.length > 0 && (
            <>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#4b5563', margin: '24px 0 8px' }}>
                Inactive users
              </div>
              <div style={s.table}>
                <div style={s.tableHeader}>
                  <span>Name</span><span>Email</span><span>Role</span><span>Status</span>
                </div>
                {inactive.map(u => (
                  <UserRow key={u.id} user={u} onClick={() => navigate(`/admin/users/${u.id}`)} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showAdd && (
        <UserFormModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function UserRow({ user, onClick }) {
  return (
    <div style={{ ...s.tableRow, cursor: 'pointer', opacity: user.active ? 1 : 0.55 }}
      onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.background = '#242b35'}
      onMouseLeave={e => e.currentTarget.style.background = ''}>
      <div>
        <div style={s.userName}>{user.name}</div>
        {user.job_title && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>{user.job_title}</div>}
      </div>
      <div style={s.cell}>{user.email}</div>
      <div><RoleBadge role={user.role} /></div>
      <div>
        {user.active
          ? <span style={{ fontSize: '10px', fontWeight: '700', color: '#86efac', background: 'rgba(134,239,172,0.08)', border: '1px solid rgba(134,239,172,0.2)', borderRadius: '6px', padding: '2px 8px' }}>Active</span>
          : <span style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', background: 'rgba(107,114,128,0.08)', border: '1px solid #2a3040', borderRadius: '6px', padding: '2px 8px' }}>Inactive</span>
        }
      </div>
    </div>
  );
}

// ── DETAIL PAGE ───────────────────────────────────────────────
export function UserDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showEdit, setShowEdit]   = useState(false);
  const [showPwd, setShowPwd]     = useState(false);
  const [toggling, setToggling]   = useState(false);
  const [error, setError]         = useState('');

  function loadUser() {
    api.get(`/api/v1/users/${id}`)
      .then(r => { setUser(r.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || 'Failed to load user'); setLoading(false); });
  }

  useEffect(() => { loadUser(); }, [id]); // eslint-disable-line

  async function toggleActive() {
    setToggling(true);
    setError('');
    try {
      await api.put(`/api/v1/users/${id}`, { active: !user.active });
      loadUser();
    } catch {
      setError('Failed to update status');
    }
    setToggling(false);
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#6b7280', fontFamily: 'system-ui', background: '#0f1117' }}>Loading...</div>
  );
  if (!user) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#fca5a5', fontFamily: 'system-ui', background: '#0f1117', flexDirection: 'column', gap: '8px' }}>
      <div>{error || 'User not found'}</div>
      <button style={{ background: 'none', border: 'none', color: '#14b8a6', cursor: 'pointer', fontSize: '13px' }} onClick={() => navigate('/admin/users')}>← Back to users</button>
    </div>
  );

  const joinedDate = user.created_at
    ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>
        <button onClick={goBack} style={s.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={s.sep}>·</span>
        <button onClick={() => navigate('/admin/users')} style={s.backBtn}>Users</button>
        <span style={s.sep}>·</span>
        <span style={s.crumb}>{user.name}</span>
      </div>

      {/* Header */}
      <div style={{ ...s.header, marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#2a3040', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', color: '#14b8a6', flexShrink: 0 }}>
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h1 style={{ ...s.title, margin: 0 }}>{user.name}</h1>
              <RoleBadge role={user.role} />
              {!user.active && <span style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', background: '#1a2030', border: '1px solid #2a3040', borderRadius: '6px', padding: '2px 8px' }}>Inactive</span>}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>{user.email}</div>
            <LastChanged type="user" id={user.id} style={{ marginTop: 6 }} />
          </div>
        </div>
        <button style={s.addBtn} onClick={() => setShowEdit(true)}>
          <i className="fa-solid fa-pen" style={{ marginRight: '6px', fontSize: '11px' }} />Edit
        </button>
      </div>

      {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>

        {/* Account info */}
        <div style={s.card}>
          <div style={s.cardTitle}>Account</div>
          <InfoRow label="Email"      value={user.email} />
          {user.job_title && <InfoRow label="Job title" value={user.job_title} />}
          {user.phone     && <InfoRow label="Phone"     value={user.phone} />}
          <InfoRow label="Role"       value={<RoleBadge role={user.role} />} />
          <InfoRow label="Status"     value={user.active ? 'Active' : 'Inactive'} valueColor={user.active ? '#86efac' : '#6b7280'} />
          <InfoRow label="Joined"     value={joinedDate} />
        </div>

        {/* Actions */}
        <div style={s.card}>
          <div style={s.cardTitle}>Actions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button style={s.actionCard} onClick={() => setShowPwd(true)}>
              <i className="fa-solid fa-key" style={{ color: '#fbbf24', fontSize: '14px' }} />
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700' }}>Reset Password</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>Set a new password for this user</div>
              </div>
            </button>
            <button
              style={{ ...s.actionCard, borderColor: user.active ? 'rgba(239,68,68,0.3)' : 'rgba(134,239,172,0.3)', opacity: toggling ? 0.6 : 1 }}
              disabled={toggling}
              onClick={toggleActive}>
              <i className={`fa-solid fa-${user.active ? 'ban' : 'circle-check'}`} style={{ color: user.active ? '#ef4444' : '#86efac', fontSize: '14px' }} />
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: user.active ? '#fca5a5' : '#86efac' }}>
                  {user.active ? 'Deactivate Account' : 'Reactivate Account'}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  {user.active ? 'User will no longer be able to sign in' : 'Restore access for this user'}
                </div>
              </div>
            </button>
          </div>
        </div>

      </div>

      {showEdit && (
        <UserFormModal
          user={user}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); loadUser(); }}
        />
      )}

      {showPwd && (
        <ResetPasswordModal
          userId={id}
          userName={user.name}
          onClose={() => setShowPwd(false)}
        />
      )}
    </div>
  );
}

function InfoRow({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '7px 0', borderBottom: '1px solid #161b22' }}>
      <span style={{ fontSize: '11px', color: '#6b7280', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '12px', fontWeight: '600', color: valueColor || '#f0f2f5', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ── ADD / EDIT MODAL ──────────────────────────────────────────
function UserFormModal({ user, onClose, onSaved }) {
  const isNew = !user;
  const [form, setForm]     = useState({
    name:      user?.name      || '',
    email:     user?.email     || '',
    role:      user?.role      || 'operator',
    job_title: user?.job_title || '',
    phone:     user?.phone     || '',
    password:  '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name: form.name, email: form.email, role: form.role, job_title: form.job_title || null, phone: form.phone || null };
      if (isNew) payload.password = form.password;
      isNew
        ? await api.post('/api/v1/users', payload)
        : await api.put(`/api/v1/users/${user.id}`, payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
      setSaving(false);
    }
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>{isNew ? 'Add User' : `Edit — ${user.name}`}</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

          <FormField label="Full name *">
            <input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" />
          </FormField>
          <FormField label="Email address *">
            <input style={s.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@company.com" />
          </FormField>
          <FormField label="Job title">
            <input style={s.input} value={form.job_title} onChange={e => set('job_title', e.target.value)} placeholder="e.g. Process Technician" />
          </FormField>
          <FormField label="Phone number">
            <input style={s.input} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="e.g. +386 40 123 456" />
          </FormField>

          <FormField label="Role">
            <div style={{ display: 'flex', gap: '8px' }}>
              {ROLES.map(r => {
                const m = ROLE_META[r];
                const active = form.role === r;
                return (
                  <button key={r} onClick={() => set('role', r)} style={{ flex: 1, padding: '10px 8px', border: `1px solid ${active ? m.border : '#2a3040'}`, borderRadius: '8px', background: active ? m.bg : '#0f1117', color: active ? m.color : '#6b7280', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: '700', fontSize: '12px', transition: 'all 0.15s' }}>
                    {m.label}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '6px' }}>
              {(ROLE_INFO[form.role] || ROLE_INFO.operator).does}
            </div>
          </FormField>

          {isNew && (
            <FormField label="Password (optional)">
              <input style={s.input} type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Leave blank to send activation email" autoComplete="new-password" />
              <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '4px' }}>
                If left blank, the user will receive an email to set their own password.
              </div>
            </FormField>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...s.addBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
            {saving ? 'Saving...' : isNew ? 'Add User' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RESET PASSWORD MODAL ──────────────────────────────────────
function ResetPasswordModal({ userId, userName, onClose }) {
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState('');

  async function save() {
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm)  { setError('Passwords do not match'); return; }
    setSaving(true); setError('');
    try {
      await api.put(`/api/v1/users/${userId}/password`, { password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
      setSaving(false);
    }
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>Reset Password</div>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>✓</div>
              <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px' }}>Password updated</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '20px' }}>{userName} can now sign in with the new password</div>
              <button style={s.addBtn} onClick={onClose}>Done</button>
            </div>
          ) : (
            <>
              {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>Set a new password for <strong style={{ color: '#f0f2f5' }}>{userName}</strong>.</div>
              <FormField label="New password">
                <input style={s.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" autoComplete="new-password" />
              </FormField>
              <FormField label="Confirm password">
                <input style={s.input} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password"
                  onKeyDown={e => e.key === 'Enter' && save()} />
              </FormField>
            </>
          )}
        </div>
        {!done && (
          <div style={s.modalFooter}>
            <button style={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button style={{ background: '#fbbf24', color: '#000', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
              {saving ? 'Saving...' : 'Set Password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
      <label style={cs.label}>{label}</label>
      {children}
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────
const COLS = '2fr 2fr 1fr 1fr';
const s = {
  ...cs,
  addBtn:      cs.btnPrimary,
  cancelBtn:   cs.btnSecondary,
  table:       cs.tableWrap,
  tableHeader: { ...cs.tableHeader, gridTemplateColumns: COLS, minWidth: '460px' },
  tableRow:    { ...cs.tableRow,    gridTemplateColumns: COLS, minWidth: '460px' },
  header:      cs.pageHeader,
  title:       cs.pageTitle,
  sub:         cs.pageSub,
  // page-specific
  userName:    { fontSize: '13px', fontWeight: '700' },
  cell:        { fontSize: '12px', color: '#9ca3af' },
  cardTitle:   { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#14b8a6', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #2a3040' },
  actionCard:  { display: 'flex', alignItems: 'center', gap: '12px', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px 14px', cursor: 'pointer', width: '100%', textAlign: 'left', color: '#f0f2f5', fontFamily: 'system-ui', transition: 'border-color 0.15s' },
  modal:       { ...cs.modal, maxWidth: '460px' },
};
